import { useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import IconButton from "@/components/IconButton";
import ObjectHeading from "@/components/ObjectHeading";
import Table, {
  TableControls,
  TableHeading,
  TableFooter,
  buildAvatarDetailsColumn,
  buildBulkSelectionColumn
} from "@/components/Table";
import Button from "@/components/Button";
import SearchInput from "@/components/SearchInput";
import EmptyState from "@/components/EmptyState";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import Icon from "@/components/Icon";
import Banner from "@/components/Banner";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import usePagination from "@/hooks/usePagination";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useNavigate } from "@/router";
import { useEventsBasePath } from "@/pages/events/_eventsBasePathContext";
import {
  STUDENTS_QUERY,
  GET_ALL_ELIGIBLE_STUDENT_IDS,
  ELIGIBLE_STUDENTS_PAGE_SIZE,
  StudentsQueryResult,
  type EligibleStudentNode
} from "./queries";
import { EXCLUDE_UNIVERSITY_EVENT_STUDENTS_MUTATION } from "./mutations";
import { FINALIZE_SEMINAR_REGISTRATIONS_MUTATION } from "@/pages/events/[eventSlug]/queries";
import RemoveStudentsDialog from "./_components/RemoveStudentsDialog";
import Dialog from "@/components/Dialog";
import { formatProgramLevel } from "../../../../_shared/constants";
import type { ColumnDef, Row, RowSelectionState } from "@tanstack/react-table";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { useHasRoles } from "@/hooks/useCapabilities";

interface EligibleStudent {
  id: string;
  name: string;
  pgaId: string;
  email: string;
  profilePhoto?: string;
  levelCode?: string;
  universityId?: string;
  universityName?: string;
}

function mapStudents(nodes: EligibleStudentNode[]): EligibleStudent[] {
  return nodes.map((p) => ({
    id: p.id,
    name: `${p.firstName} ${p.lastName}`,
    pgaId: p.pgaId,
    email: p.email,
    profilePhoto: p.profilePhoto,
    levelCode: p.educationLatestPgmProgram?.levelCode,
    universityId: p.university?.id ?? p.lastAttendedPgmUniversity?.id,
    universityName: p.university?.name ?? p.lastAttendedPgmUniversity?.name
  }));
}

export default function ManualRegistrationsPage() {
  const { eventSlug = "", universityId = "" } = useParams();
  const basePath = useEventsBasePath();
  const navigate = useNavigate();
  const { isCompactLayout } = useIsCompactLayout();
  const isEducationHq = useHasRoles([ROLE.EDUCATION_ADMIN, ROLE.GLOBAL_ADMIN]);
  const universityLegacyIds = useMemo(
    () => (isEducationHq ? [] : universityId ? [universityId] : []),
    [isEducationHq, universityId]
  );

  const [selectedPgaId, setSelectedPgaId] = useState<string | null>(null);
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);
  const [isBulkRemoveModalOpen, setIsBulkRemoveModalOpen] = useState(false);
  const [excludedStudentIds, setExcludedStudentIds] = useState<string[]>([]);

  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { rowSelection, setRowSelection, bulkSelectionProps, isSelectedOnly, deselectAll, rowSelectionCount } =
    useBulkSelection();

  const searchFilter = searchValue.trim() || undefined;

  const { data, loading, refetch } = useQuery<StudentsQueryResult>(STUDENTS_QUERY, {
    variables: {
      eventSlug,
      universityLegacyIds,
      includeUniversity: isEducationHq,
      search: searchFilter,
      ...paginationVariables
    },
    skip: !eventSlug || (!universityId && !isEducationHq)
  });

  const [fetchAllEligibleStudentIds, { loading: fetchingAllEligibleStudentIds }] = useLazyQuery(
    GET_ALL_ELIGIBLE_STUDENT_IDS,
    {
      fetchPolicy: "network-only"
    }
  );

  const fetchAllEligibleStudentNodes = useCallback(async () => {
    const collected: Array<Pick<EligibleStudentNode, "id" | "university" | "lastAttendedPgmUniversity">> = [];
    let after: string | undefined;

    for (;;) {
      const { data: pageData } = await fetchAllEligibleStudentIds({
        variables: {
          eventSlug,
          universityLegacyIds,
          includeUniversity: isEducationHq,
          search: searchFilter,
          first: ELIGIBLE_STUDENTS_PAGE_SIZE,
          after
        }
      });
      const connection = pageData?.universityStudents;
      collected.push(...(connection?.nodes ?? []));
      if (!connection?.pageInfo?.hasNextPage || !connection.pageInfo.endCursor) {
        break;
      }
      after = connection.pageInfo.endCursor;
    }

    return collected;
  }, [eventSlug, universityLegacyIds, isEducationHq, searchFilter, fetchAllEligibleStudentIds]);

  const event = data?.event;
  const matchedUniversity = useMemo(
    () => event?.universities?.find((u) => u.id === universityId || u.legacyId === universityId),
    [event?.universities, universityId]
  );
  const universityLegacyId = matchedUniversity?.legacyId ?? universityId;
  const isMissingPageContext =
    !loading && Boolean(eventSlug) && (!event?.id || (!isEducationHq && !universityLegacyId));
  const resolvedUniversityId = matchedUniversity?.id ?? "";
  const eligibleStudentsConnection = data?.universityStudents;

  const allStudents = useMemo(() => {
    const nodes = eligibleStudentsConnection?.nodes ?? [];
    return mapStudents(nodes).filter((s) => !excludedStudentIds.includes(s.id));
  }, [eligibleStudentsConnection, excludedStudentIds]);

  const filteredStudents = useMemo(() => {
    let students = allStudents;
    if (isSelectedOnly) {
      students = students.filter((s) => rowSelection[s.id]);
    }
    return students;
  }, [allStudents, isSelectedOnly, rowSelection]);

  const showUniversityColumn = isEducationHq;

  const [finalizeSeminarRegistrations, { loading: finalizeLoading }] = useMutation(
    FINALIZE_SEMINAR_REGISTRATIONS_MUTATION
  );
  const [excludeUniversityEventStudents, { loading: excludeBulkStudentsLoading }] = useMutation(
    EXCLUDE_UNIVERSITY_EVENT_STUDENTS_MUTATION
  );

  const handleFinalize = () => {
    setIsFinalizeModalOpen(true);
  };

  const handleConfirmFinalize = async () => {
    const eventId = event?.id;
    if (!eventId || (!isEducationHq && !universityLegacyId)) {
      return;
    }

    try {
      const { data: mutationData } = await finalizeSeminarRegistrations({
        variables: {
          input: isEducationHq ? { eventId } : { universityLegacyId, eventId }
        }
      });
      const payload = mutationData?.finalizeSeminarRegistrations;
      setIsFinalizeModalOpen(false);

      if (!payload?.success) {
        showErrorSnackbar(payload?.message ?? "Could not finalize registrations.");
        return;
      }

      const parts: string[] = [];
      if (payload.registeredCount > 0) {
        parts.push(`Registered ${payload.registeredCount} student${payload.registeredCount !== 1 ? "s" : ""}`);
      }
      if (payload.alreadyRegisteredCount > 0) {
        parts.push(`${payload.alreadyRegisteredCount} already registered`);
      }
      if (payload.failedCount > 0) {
        parts.push(`${payload.failedCount} failed`);
      }
      showSnackbar(parts.length > 0 ? `${parts.join(". ")}.` : (payload.message ?? "Registrations finalized."), {
        close: true
      });
      void refetch();
      navigate(`${basePath}/${eventSlug}/registrations` as any);
    } catch {
      setIsFinalizeModalOpen(false);
      showErrorSnackbar("Could not finalize registrations. Try again.");
    }
  };

  const handleCancel = () => {
    navigate(`${basePath}/${eventSlug}/registrations` as any);
  };

  const handleViewStudent = useCallback((student: EligibleStudent) => {
    setSelectedPgaId(student.pgaId);
    setIsPersonSideSheetOpen(true);
  }, []);

  const handleConfirmBulkRemoveStudents = useCallback(async () => {
    const idsToRemove = Object.keys(rowSelection).filter((id) => rowSelection[id]);
    if (idsToRemove.length === 0) return;
    if (!eventSlug) {
      showErrorSnackbar("Unable to remove students: university context is missing.");
      return;
    }

    try {
      const allowRouteUniversityFallback = !isEducationHq;
      const mappedUniversityIdForExclude = (studentUniversityId?: string) => {
        if (studentUniversityId) return studentUniversityId;
        if (!allowRouteUniversityFallback) return undefined;
        return resolvedUniversityId || undefined;
      };

      const universityIdByPersonId = new Map<string, string>();
      for (const student of allStudents) {
        const mappedUniversityId = mappedUniversityIdForExclude(student.universityId);
        if (mappedUniversityId) {
          universityIdByPersonId.set(student.id, mappedUniversityId);
        }
      }

      const missingFromPage = idsToRemove.filter((id) => !universityIdByPersonId.has(id));
      if (missingFromPage.length > 0) {
        const allEligibleNodes = await fetchAllEligibleStudentNodes();
        for (const node of allEligibleNodes) {
          const mappedUniversityId = mappedUniversityIdForExclude(
            node.university?.id ?? node.lastAttendedPgmUniversity?.id
          );
          if (mappedUniversityId) {
            universityIdByPersonId.set(node.id, mappedUniversityId);
          }
        }
      }

      const personIdsByUniversityId = new Map<string, string[]>();
      for (const personId of idsToRemove) {
        const excludeUniversityId = universityIdByPersonId.get(personId);
        if (!excludeUniversityId) {
          showErrorSnackbar("Unable to remove students: university context is missing.");
          return;
        }
        const personIds = personIdsByUniversityId.get(excludeUniversityId) ?? [];
        personIds.push(personId);
        personIdsByUniversityId.set(excludeUniversityId, personIds);
      }

      const successfullyExcludedIds: string[] = [];
      let failureMessage: string | undefined;

      for (const [excludeUniversityId, personIds] of personIdsByUniversityId) {
        const { data: excludeData } = await excludeUniversityEventStudents({
          variables: {
            input: {
              universityId: excludeUniversityId,
              eventSlug,
              personIds
            }
          }
        });
        const payload = excludeData?.excludeUniversityEventStudents;
        if (!payload?.success) {
          failureMessage = payload?.message ?? "Could not remove students from this list.";
          break;
        }
        successfullyExcludedIds.push(...personIds);
      }

      if (successfullyExcludedIds.length > 0) {
        setExcludedStudentIds((prev) => [...new Set([...prev, ...successfullyExcludedIds])]);
        setRowSelection((prev) => {
          const next = { ...prev };
          successfullyExcludedIds.forEach((id) => {
            delete next[id];
          });
          return next;
        });
      }

      if (failureMessage) {
        void refetch();
        showErrorSnackbar(failureMessage);
        return;
      }

      setIsBulkRemoveModalOpen(false);
      void refetch();
      showSnackbar(
        `${successfullyExcludedIds.length} student${successfullyExcludedIds.length !== 1 ? "s" : ""} removed from this list.`,
        { close: true }
      );
    } catch {
      void refetch();
      showErrorSnackbar("Could not remove students from this list. Try again.");
    }
  }, [
    rowSelection,
    setRowSelection,
    allStudents,
    resolvedUniversityId,
    isEducationHq,
    eventSlug,
    fetchAllEligibleStudentNodes,
    excludeUniversityEventStudents,
    refetch
  ]);

  const handleSelectAllAcrossPages = useCallback(async () => {
    if (!eventSlug || (!universityId && !isEducationHq)) return;

    const nodes = await fetchAllEligibleStudentNodes();
    const newSelection: RowSelectionState = {};
    for (const node of nodes) {
      newSelection[node.id] = true;
    }
    setRowSelection(newSelection);
  }, [eventSlug, universityId, isEducationHq, fetchAllEligibleStudentNodes, setRowSelection]);

  const columns: ColumnDef<EligibleStudent, any>[] = [
    buildBulkSelectionColumn<EligibleStudent>(),
    ...buildAvatarDetailsColumn<EligibleStudent>({
      isCompactLayout,
      getTitle: (row) => row.name,
      getSubtitle: (row) => row.pgaId,
      subtitleLabel: "PGA ID",
      getPicture: (row) => row.profilePhoto,
      header: "Name"
    }),
    { accessorKey: "pgaId", header: "PGA ID", enableSorting: false },
    { accessorKey: "email", header: "Email", enableSorting: false },
    ...(showUniversityColumn
      ? [
          {
            accessorKey: "universityName",
            header: "University",
            cell: ({ row }: { row: Row<EligibleStudent> }) => <span>{row.original.universityName || "—"}</span>,
            enableSorting: false
          }
        ]
      : []),
    {
      accessorKey: "levelCode",
      header: "Program / Level",
      cell: ({ row }: { row: Row<EligibleStudent> }) => {
        const levelCode = row.original.levelCode;
        if (!levelCode) return <span>—</span>;
        return (
          <div className="flex items-center gap-2">
            <span>{formatProgramLevel(levelCode)}</span>
          </div>
        );
      },
      enableSorting: true
    },
    {
      id: "view",
      header: "",
      cell: ({ row }: { row: Row<EligibleStudent> }) => (
        <button
          className="p-2 rounded-full hover:bg-sys-brand-surface-container"
          onClick={() => handleViewStudent(row.original)}
          title="View Student"
        >
          <Icon name="open_in_full" size={20} className="text-sys-brand-on-surface-variant" />
        </button>
      ),
      enableSorting: false,
      size: 56,
      minSize: 56,
      maxSize: 56
    }
  ];

  return (
    <FocusedPageLayout
      cancelButton={
        <div className="flex cursor-pointer items-center" onClick={handleCancel} data-testid="cancel-button">
          <IconButton name="arrow_back" iconClassName="text-sys-brand-on-surface" />
          <span className="text-title-large">Cancel</span>
        </div>
      }
      actions={
        <Button
          variant="filled"
          onClick={handleFinalize}
          disabled={finalizeLoading || isMissingPageContext || (eligibleStudentsConnection?.totalCount ?? 0) === 0}
          testId="finalize-registrations-button"
        >
          Finalize Registrations
        </Button>
      }
      stickyActions={true}
    >
      <ObjectHeading
        title={event?.title ?? ""}
        metaData={[{ text: `${eligibleStudentsConnection?.totalCount ?? 0}`, label: "New Registrations" }]}
        iconName="today"
        isDetailsView
        showFullMetadataInCondensed={true}
      />

      {isMissingPageContext && (
        <Banner
          className="mt-6"
          variant="warn"
          description="Missing university or event. Try refreshing the page."
          testId="manual-registrations-missing-context-banner"
        />
      )}

      <TableHeading
        heading="Eligible Students"
        body="All eligible students are listed below. Remove anyone who should not be registered, then finalize to register every remaining eligible student for this event."
      />

      <Table
        data={filteredStudents}
        columns={columns}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        loading={loading}
        hasActiveFilters={!!searchFilter || isSelectedOnly}
        renderNoDataEmptyState={() => (
          <EmptyState
            iconName="groups"
            title="No Eligible Students"
            caption="No students are currently eligible for this event."
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState
            iconName="groups"
            title="No Eligible Students"
            caption="No students are currently eligible for this event."
          />
        )}
        renderHeader={(table) => (
          <TableControls
            pagination={!isSelectedOnly ? createHeaderPagination(eligibleStudentsConnection as any) : undefined}
            {...bulkSelectionProps}
            onDeselectAll={deselectAll}
            totalRowCount={rowSelectionCount}
            onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
            allRowsSelected={table.getIsAllRowsSelected()}
            someRowsSelected={table.getIsSomeRowsSelected()}
            onSelectAllAcrossPages={handleSelectAllAcrossPages}
            selectAllAcrossPagesLoading={fetchingAllEligibleStudentIds}
            totalMatchingCount={eligibleStudentsConnection?.totalCount ?? 0}
            useExpandedFilters={true}
            bulkActions={[
              {
                label: "Remove Selected",
                icon: "person_remove",
                onClick: () => setIsBulkRemoveModalOpen(true),
                variant: "outlined"
              }
            ]}
          >
            <SearchInput
              query={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                resetPagination();
              }}
              onClear={() => {
                setSearchValue("");
                resetPagination();
              }}
              placeholder="Search Students"
            />
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={!isSelectedOnly ? createFooterPagination(eligibleStudentsConnection as any) : undefined}
            {...bulkSelectionProps}
          />
        )}
      />
      {isPersonSideSheetOpen && selectedPgaId && (
        <PersonSideSheet
          pgaId={selectedPgaId}
          open={isPersonSideSheetOpen}
          onClose={() => setIsPersonSideSheetOpen(false)}
        />
      )}

      <RemoveStudentsDialog
        open={isBulkRemoveModalOpen}
        count={rowSelectionCount}
        onClose={() => setIsBulkRemoveModalOpen(false)}
        onConfirm={handleConfirmBulkRemoveStudents}
        confirmLoading={excludeBulkStudentsLoading}
      />

      <Dialog
        headline="Finalize Registrations"
        open={isFinalizeModalOpen}
        onClosed={() => setIsFinalizeModalOpen(false)}
        actions={
          <>
            <Button variant="text" onClick={() => setIsFinalizeModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="filled"
              icon="check"
              onClick={handleConfirmFinalize}
              disabled={finalizeLoading}
              testId="confirm-finalize-button"
            >
              Finalize
            </Button>
          </>
        }
      >
        <p className="text-body-medium text-sys-brand-on-surface">
          <strong>Registrations to Finalize:</strong> {eligibleStudentsConnection?.totalCount ?? 0}
        </p>
        <p className="text-body-medium text-sys-brand-on-surface mt-4">
          Are you sure you wish to finalize these registrants?
        </p>
      </Dialog>
    </FocusedPageLayout>
  );
}
